import { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

/**
 * useMiaEventMessageData (Data Layer)
 * Extrae y memoiza los datos puros para MiaEventMessageMC.
 *
 * @param {Object} params
 * @param {Object} params.message - Mensaje con eventData o assignmentData
 * @returns {Object} { name, formattedDateTime, field, i18nKey }
 */
export default function useMiaEventMessageData({ message }) {
  const { t, i18n } = useTranslation();

  // Memoiza el nombre a mostrar (agente MIA o asignación)
  const name = useMemo(() => {
    if (message?.eventData?.agent_name) {
      return message.eventData.agent_name.includes('mia_')
        ? t(`chatbot_ai.general.activity.event.${message.eventData.agent_name}`, { tag_name: message.eventData.tag_name })
        : message.eventData.agent_name;
    }
    if (
      message?.assignmentData?.name &&
      (message.assignmentData.assignable_type === 'team' || message.assignmentData.assignable_type === 'agent')
    ) {
      return message.assignmentData.name;
    }
    return '';
  }, [
    message?.eventData?.agent_name,
    message?.eventData?.tag_name,
    message?.assignmentData?.name,
    message?.assignmentData?.assignable_type,
    t
  ]);

  // Memoiza el formato de fecha/hora
  const formattedDateTime = useMemo(() => {
    if (!message?.createdTime) return '';
    return moment(message.createdTime).local().locale(i18n.language).format('DD-MM-YYYY HH:mm');
  }, [message?.createdTime, i18n.language]);

  // Campo relevante para el tipo de mensaje
  const field = useMemo(() => {
    if (message?.eventData?.action) return 'agent_name';
    if (message?.assignmentData) return 'name';
    return null;
  }, [message?.eventData?.action, message?.assignmentData]);

  // i18nKey para el mensaje
  const i18nKey = useMemo(() => {
    if (message?.eventData?.action) return `chatbot_ai.general.activity.event.${message.eventData.action}`;
    if (message?.assignmentData) return 'chatbot_ai.general.activity.assignments';
    return null;
  }, [message?.eventData?.action, message?.assignmentData]);

  return { name, formattedDateTime, field, i18nKey };
}
